"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.timeSlotsToServices = timeSlotsToServices;
exports.applyTsDocsToMonthAvailability = applyTsDocsToMonthAvailability;
exports.unpackMonth = unpackMonth;
exports.cutoffDateStrs = cutoffDateStrs;
exports.unpackDates = unpackDates;
const first_1 = __importDefault(require("lodash/first"));
const flatMap_1 = __importDefault(require("lodash/flatMap"));
const groupBy_1 = __importDefault(require("lodash/groupBy"));
const isEmpty_1 = __importDefault(require("lodash/isEmpty"));
const last_1 = __importDefault(require("lodash/last"));
const range_1 = __importDefault(require("lodash/range"));
const reduce_1 = __importDefault(require("lodash/reduce"));
const sortBy_1 = __importDefault(require("lodash/sortBy"));
const sortedUniq_1 = __importDefault(require("lodash/sortedUniq"));
const toPairs_1 = __importDefault(require("lodash/toPairs"));
const values_1 = __importDefault(require("lodash/values"));
const luxon_1 = require("luxon");
const TDR_1 = require("../types/TDR");
const reservation_1 = require("./reservation");
const time_1 = require("./time");
const defaultServiceName = (0, last_1.default)(TDR_1.TDR.DEFAULT_SERVICE_NAMES);
const defaultSeatingName = 'First Seating';
function slotPriceTier(slot) {
    var _a;
    return ((_a = slot.priceTier) !== null && _a !== void 0 ? _a : Math.ceil(slot.regularPrice / 10));
}
/**
 * Converts raw timeslot data into a structured service/seating hierarchy.
 *
 * Groups timeslots by service (e.g., "Dinner", "Lunch") and seating (e.g., "First Seating"),
 * calculating availability and metadata for each grouping. Used for displaying
 * service options in the booking flow.
 *
 * @param timeSlotMaps - Array of timeslot maps from multiple tables
 * @returns Sorted array of services, each containing seatings with their timeslots
 *
 * @example
 * const services = timeSlotsToServices([
 *   { '17:00': { service: 'Dinner', seating: 'First', ... } },
 *   { '20:00': { service: 'Dinner', seating: 'Second', ... } }
 * ]);
 * // Returns: [{ name: 'Dinner', seatings: [...] }]
 */
function timeSlotsToServices(timeSlotMaps) {
    const slots = (0, flatMap_1.default)(timeSlotMaps, timeSlots => (0, toPairs_1.default)(timeSlots).map(([time, slot]) => [time, Object.assign(Object.assign({}, slot), { service: slot.service || defaultServiceName, seating: slot.seating || slot.timeLabel || defaultSeatingName })]));
    const seatingSlots = (0, values_1.default)((0, groupBy_1.default)(slots, ([, slot]) => `${slot.service}_${slot.seating}`));
    const seatings = seatingSlots.map(timesAndSlots => {
        const times = (0, sortedUniq_1.default)(timesAndSlots.map(ts => ts[0]).sort());
        const startTime = (0, first_1.default)(times);
        const endTime = (0, last_1.default)(times);
        const slots = timesAndSlots.map(ts => ts[1]);
        const { service, seating } = slots[0];
        const availableSlots = slots.filter(slot => slot.available);
        const available = !(0, isEmpty_1.default)(availableSlots);
        return {
            name: seating,
            service,
            startTime,
            endTime,
            available,
            priceTier: availableSlots.reduce((total, slot) => slotPriceTier(slot) + total, 0) / (availableSlots.length || 1),
            timeSlots: timesAndSlots.reduce((agg, [time, slot]) => {
                if (slot.available) {
                    agg[time] = slot;
                }
                return agg;
            }, {})
        };
    });
    const services = (0, values_1.default)((0, groupBy_1.default)(seatings, seating => seating.service)).map(seatings => ({
        name: seatings[0].service,
        seatings: (0, sortBy_1.default)(seatings, [seating => seating.startTime])
    }));
    return (0, sortBy_1.default)(services, service => service.seatings[0].startTime);
}
/**
 * Calculates table availability metrics for a single date.
 *
 * Uses a greedy first-fit algorithm to estimate how many reservations can fit.
 * Note: This is a heuristic and may underestimate capacity by 5-10% compared to
 * optimal packing, but is sufficient for availability indicators (green/yellow/red).
 *
 * @param tsDoc - TimeSlot document for a specific table and date
 * @returns Object with:
 *   - available: Estimated reservations possible from currently available slots
 *   - total: Estimated reservations possible from all slots (available and unavailable)
 *
 * Note: The algorithm skips unavailable slots in both calculations, so `total` represents
 * capacity considering booked slots as obstacles, not theoretical maximum if all were available.
 */
function timeSlotDocToTableAvailability(tsDoc) {
    const orderedSlots = (0, sortBy_1.default)((0, toPairs_1.default)(tsDoc.timeSlots), ([time]) => time);
    const availableSlots = orderedSlots.filter(([, slot]) => slot.available);
    return {
        available: countPossibleReservations(availableSlots),
        total: countPossibleReservations(orderedSlots)
    };
}
/**
 * Estimates how many reservations can fit in a given set of time slots.
 *
 * Uses a greedy first-fit algorithm:
 * 1. Takes the earliest available slot
 * 2. Blocks time until the reservation duration expires
 * 3. Takes the next available slot after that duration
 * 4. Repeats until no more slots fit
 *
 * This is a heuristic solution to an NP-hard bin packing problem. The greedy
 * approach may underestimate capacity by 5-10% compared to optimal packing,
 * but is computationally efficient (O(n²) worst case, O(n) typical) and
 * sufficient for availability indicators.
 *
 * Time Complexity: O(n²) worst case when all slots are tightly spaced
 * Space Complexity: O(1)
 *
 * @param timesAndSlots - Array of [timeString, slot] tuples, should be sorted by time
 * @returns Estimated number of reservations that can fit
 *
 * @example
 * // With 90-minute reservations every 30 minutes
 * countPossibleReservations([
 *   ['17:00', { resDuration: 90, ... }],
 *   ['17:30', { resDuration: 90, ... }],
 *   ['18:00', { resDuration: 90, ... }],
 *   ['19:00', { resDuration: 90, ... }]
 * ]) // Returns 2: takes 17:00 (blocks until 18:30), then 19:00
 */
function countPossibleReservations(timesAndSlots) {
    let count = 0;
    let currentIndex = 0;
    while (currentIndex < timesAndSlots.length) {
        const [time, slot] = timesAndSlots[currentIndex];
        if (!slot) {
            break;
        }
        count++;
        const nextOpenTimeStr = (0, time_1.addMinutesToTimeStr)(time, slot.resDuration || time_1.DEFAULT_RESERVATION_TIME_BUFFER.minutesAfter);
        // Find next available slot after reservation duration
        let foundNext = false;
        for (let i = currentIndex + 1; i < timesAndSlots.length; i++) {
            if (timesAndSlots[i][0] >= nextOpenTimeStr) {
                currentIndex = i;
                foundNext = true;
                break;
            }
        }
        if (!foundNext) {
            break;
        }
    }
    return count;
}
/**
 * Applies timeslot documents to a month availability structure, computing
 * capacity metrics for each table on each date.
 *
 * This function is called when:
 * - Building a month's availability cache (buildMonth)
 * - Updating availability after reservation changes
 * - Applying cutoff date recalculations
 *
 * @param tsDocs - Array of TimeSlot documents to process
 * @param month - Optional existing month structure to update (if omitted, creates new)
 * @returns Updated month availability with capacity metrics per table per date
 *
 * @example
 * const tsDocs = [
 *   { tableId: 'table1', dateStr: '2024-01-15', timeSlots: {...} },
 *   { tableId: 'table2', dateStr: '2024-01-15', timeSlots: {...} }
 * ];
 * const monthAvail = applyTsDocsToMonthAvailability(tsDocs);
 * // monthAvail.dates[14].tables['table1'] = { available: 3, total: 5 }
 */
function applyTsDocsToMonthAvailability(tsDocs, month) {
    var _a, _b, _c;
    const restaurantId = (month === null || month === void 0 ? void 0 : month.restaurantId) || ((_a = tsDocs === null || tsDocs === void 0 ? void 0 : tsDocs[0]) === null || _a === void 0 ? void 0 : _a.restaurantId);
    const monthStr = (month === null || month === void 0 ? void 0 : month.monthStr) || ((_c = (_b = tsDocs === null || tsDocs === void 0 ? void 0 : tsDocs[0]) === null || _b === void 0 ? void 0 : _b.dateStr) === null || _c === void 0 ? void 0 : _c.slice(0, 7));
    const dates = (month === null || month === void 0 ? void 0 : month.dates) || [];
    const updatedMonth = (0, reduce_1.default)(tsDocs, (month, tsDoc) => {
        const { tableId, dateStr } = tsDoc;
        const date = parseInt(dateStr.slice(8));
        const dateAvail = month.dates[date - 1] || { dateStr, tables: {} };
        dateAvail.tables[tableId] = timeSlotDocToTableAvailability(tsDoc);
        month.dates[date - 1] = dateAvail;
        // console.log({tableId, dateStr, date, dateAvail})
        return month;
    }, { restaurantId, monthStr, dates });
    // console.log('applyTsDocsToMonthAvailability', {tsDocs, month, updatedMonth})
    return updatedMonth;
}
function daysInMonth(monthStr) {
    const month = luxon_1.DateTime.fromFormat(`${monthStr}-01`, time_1.DATESTR_FMT);
    const nextMonth = month.plus({ month: 1 });
    return Math.abs(month.diff(nextMonth, 'days').days);
}
const zpad = (n) => ('00' + n).slice(-2);
function unpackMonth(monthStr, restaurant, tables, dbTsDocs, opts) {
    const dateStrs = (0, range_1.default)(1, daysInMonth(monthStr) + 1).map(date => `${monthStr}-${zpad(date)}`);
    return unpackDates(dateStrs, restaurant, tables, dbTsDocs, opts);
}
function cutoffDateStrs(selectedMonth, max, restaurantTimezone) {
    const now = luxon_1.DateTime.local().setZone(restaurantTimezone);
    const monthStart = luxon_1.DateTime.fromFormat(selectedMonth, time_1.MONTHSTR_FMT);
    const start = now > monthStart ? now : monthStart;
    if (start.toISO() > max.toISO()) {
        return [];
    }
    const days = Math.ceil(Math.abs(start.diff(max, 'days').days));
    return (0, range_1.default)(days + 1).map(day => start.plus({ day }).toFormat(time_1.DATESTR_FMT)).filter(ds => ds.startsWith(selectedMonth));
    // The days+1 is important, always return at least 1 result to calculate today as cutoff date.
}
function unpackDates(dateStrs, restaurant, tables, dbTsDocs, opts) {
    const dbTsDocsByTableDate = dbTsDocs.reduce((agg, dbTsDoc) => {
        const { tableId, dateStr } = dbTsDoc;
        agg[tableId] = agg[tableId] || {};
        agg[tableId][dateStr] = dbTsDoc;
        return agg;
    }, {});
    const results = (0, flatMap_1.default)(tables, table => dateStrs.map(dateStr => {
        var _a;
        const dbTsDoc = ((_a = dbTsDocsByTableDate[table.id]) === null || _a === void 0 ? void 0 : _a[dateStr]) || { dateStr };
        return (0, reservation_1.unpackTimeSlots)(restaurant, table, dbTsDoc, opts);
    }));
    // console.log('unpack dates', { dateStrs, tables, dbTsDocs, dbTsDocsByTableDate, results })
    return results;
}
//# sourceMappingURL=services.js.map